import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Avatar, Box, Link, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { format } from 'date-fns';
import { es, enUS, ca, pt, de, fr, Locale } from 'date-fns/locale';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import GroupIcon from '@mui/icons-material/Group';
import { useTranslation } from 'react-i18next';
import {
    AssignmentInd,
    LightbulbOutlined as LightsIcon,
    Block as ApbIcon,
    ConfirmationNumber as ApbBonosIcon,
    MeetingRoom as DoorOpenIcon,
    SettingsRemote as RemoteIcon,
} from '@mui/icons-material';
import {
    AppContext,
    SttChipFacilityRow,
    SttTable,
    SttTopControls,
    SttPage,
} from '../../../sporttia/all';
import { useToggle, useTuples } from '../../../lib/hooks';
import { sporttiaColors } from '../../../theme/theme';
import { DevicesKeysDialog } from './DevicesKeysDialog';
import DeviceDetailsDialog from './DeviceDetailsDialog';
import { DevicesAuthorizationsDialog } from './DevicesAuthorizationsDialog';
import {
    SttChipDeviceType,
    DeviceType,
} from '../../../sporttia/chips/SttChipDeviceType';
import DevicesSpacesDialog from '../capacity/Capacities';

type Filters = {
    name: string;
};

type Device = {
    id: string;
    name: string;
    type?: string;
    types?: Array<{ type: DeviceType }>;
    lightOn?: boolean;
    antiPassback?: boolean;
    antiPassbackBonos?: boolean;
    foorOpen?: boolean;
    remoteOpening?: boolean;
    counter?: number;
    localDate?: string;
    facilities?: Array<{ id: string; name: string }>;
};

type DevicesResponse = {
    data: Device[];
    total: number;
};

// Disabled icon colors
const DISABLED_COLOR = sporttiaColors.icon.disabled;
const DISABLED_BG = sporttiaColors.icon.disabledBg;

const localeMap: Record<string, Locale> = {
    es,
    en: enUS,
    ca,
    pt,
    de,
    fr,
    mx: es,
};

export default function Devices(): React.ReactElement {
    const { t, i18n } = useTranslation();
    const cxt = useContext(AppContext)!;
    const { id: urlId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    // Determine initial tab from URL path
    const getInitialTabFromUrl = (): number => {
        if (location.pathname.endsWith('/authorizations')) return 1;
        if (location.pathname.endsWith('/accesses')) return 2;
        return 0;
    };

    const [devices, setDevices] = useState<DevicesResponse | null>(null);
    const [filters, setFilter] = useTuples<Filters>({
        name: '',
    });

    const [trash, toggleTrash] = useToggle(() => {
        loadDevices();
    });
    const [openKeysDialog, setOpenKeysDialog] = useState(false);
    const [openAuthorizations, setOpenAuthorizations] = useState(false);
    const [openDevicesSpaces, setOpenDevicesSpaces] = useState(false);
    const [deviceIdSelected, setDeviceIdSelected] = useState<string | null>(
        urlId ?? null,
    );

    // Handle device ID from URL path parameter changes
    useEffect(() => {
        if (urlId && urlId !== deviceIdSelected) {
            setDeviceIdSelected(urlId);
        }
    }, [urlId]);

    useEffect(() => {
        cxt?.setHeader({ title: t('Devices') });
    }, []);

    const formatDate = useCallback(
        (date?: string): string => {
            if (!date) return '';
            const d = new Date(date);
            const locale = localeMap[i18n.language] || es;
            const isCurrentYear = d.getFullYear() === new Date().getFullYear();
            return format(d, isCurrentYear ? 'd MMM' : 'd MMM yyyy', {
                locale,
            });
        },
        [i18n.language],
    );

    const loadDevices = useCallback(
        (p = { page: 1, rows: 20 }): void => {
            cxt?.api('GET', `/scs/${cxt?.sc?.id}/devices`, {
                params: {
                    page: p.page,
                    rows: p.rows,
                    ...filters,
                    trash,
                },
                success: (r: DevicesResponse) => {
                    setDevices(r);
                },
            });
        },
        [cxt, filters, trash],
    );

    const handleFilterChange = useCallback(
        ({ name, value }: { name: string | undefined; value: unknown }) => {
            if (!name) return;
            setFilter({ name, value });
        },
        [setFilter],
    );

    const openDeviceDetails = useCallback((device: Device) => {
        setDeviceIdSelected(device.id);
    }, []);

    const closeDeviceDetails = useCallback(() => {
        setDeviceIdSelected(null);
        loadDevices();
        // Navigate back to devices list if we came from a direct URL
        if (urlId) {
            navigate('/devices');
        }
    }, [loadDevices, urlId, navigate]);

    const handleDeviceTabChange = useCallback(
        (tabIndex: number) => {
            if (urlId && deviceIdSelected) {
                const tabPaths = ['', '/authorizations', '/accesses'];
                navigate(`/devices/${deviceIdSelected}${tabPaths[tabIndex]}`, {
                    replace: true,
                });
            }
        },
        [urlId, deviceIdSelected, navigate],
    );

    const iconButtons = useMemo(
        () => [
            {
                icon: <VpnKeyIcon />,
                onClick: () => setOpenKeysDialog(true),
            },
            {
                icon: <AssignmentInd />,
                onClick: () => setOpenAuthorizations(true),
            },
            {
                icon: <GroupIcon />,
                onClick: () => setOpenDevicesSpaces(true),
            },
        ],
        [],
    );

    const columns = useMemo(
        () => [
            {
                title: t('Code'),
                field: 'id',
                align: 'center' as const,
            },
            {
                align: 'left' as const,
                title: t('Name'),
                value: (row: Device) => (
                    <Link
                        sx={{
                            color: 'var(--brand-text)',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={() => openDeviceDetails(row)}
                    >
                        {row.name}
                    </Link>
                ),
            },
            {
                title: t('Type'),
                align: 'center' as const,
                value: (row: Device) => (
                    <Box display="flex" gap={0.5} justifyContent="center">
                        <SttChipDeviceType
                            types={
                                row.types ||
                                (row.type
                                    ? [{ type: row.type as DeviceType }]
                                    : [])
                            }
                        />
                    </Box>
                ),
            },
            {
                title: t('Configuration'),
                value: (row: Device) => (
                    <Box display="flex" gap={0.5} justifyContent="center">
                        <Tooltip title={t('Lights')}>
                            <Avatar
                                sx={{
                                    width: 28,
                                    height: 28,
                                    bgcolor: row.lightOn
                                        ? alpha(
                                              sporttiaColors.warning.main,
                                              0.15,
                                          )
                                        : DISABLED_BG,
                                }}
                            >
                                <LightsIcon
                                    sx={{
                                        color: row.lightOn
                                            ? 'warning.main'
                                            : DISABLED_COLOR,
                                        fontSize: 18,
                                    }}
                                />
                            </Avatar>
                        </Tooltip>
                        <Tooltip title={t('AntiPassback')}>
                            <Avatar
                                sx={{
                                    width: 28,
                                    height: 28,
                                    bgcolor: row.antiPassback
                                        ? alpha(sporttiaColors.error.main, 0.15)
                                        : DISABLED_BG,
                                }}
                            >
                                <ApbIcon
                                    sx={{
                                        color: row.antiPassback
                                            ? 'error.main'
                                            : DISABLED_COLOR,
                                        fontSize: 18,
                                    }}
                                />
                            </Avatar>
                        </Tooltip>
                        <Tooltip title={t('AntiPassbackBonos')}>
                            <Avatar
                                sx={{
                                    width: 28,
                                    height: 28,
                                    bgcolor:
                                        row.antiPassback &&
                                        row.antiPassbackBonos
                                            ? alpha(
                                                  sporttiaColors.error.main,
                                                  0.15,
                                              )
                                            : DISABLED_BG,
                                }}
                            >
                                <ApbBonosIcon
                                    sx={{
                                        color:
                                            row.antiPassback &&
                                            row.antiPassbackBonos
                                                ? 'error.main'
                                                : DISABLED_COLOR,
                                        fontSize: 18,
                                    }}
                                />
                            </Avatar>
                        </Tooltip>
                        <Tooltip title={t('Open')}>
                            <Avatar
                                sx={{
                                    width: 28,
                                    height: 28,
                                    bgcolor: row.foorOpen
                                        ? alpha(
                                              sporttiaColors.success.main,
                                              0.15,
                                          )
                                        : DISABLED_BG,
                                }}
                            >
                                <DoorOpenIcon
                                    sx={{
                                        color: row.foorOpen
                                            ? 'success.main'
                                            : DISABLED_COLOR,
                                        fontSize: 18,
                                    }}
                                />
                            </Avatar>
                        </Tooltip>
                        <Tooltip title={t('RemoteOpening')}>
                            <Avatar
                                sx={{
                                    width: 28,
                                    height: 28,
                                    bgcolor: row.remoteOpening
                                        ? alpha(sporttiaColors.info.main, 0.15)
                                        : DISABLED_BG,
                                }}
                            >
                                <RemoteIcon
                                    sx={{
                                        color: row.remoteOpening
                                            ? 'info.main'
                                            : DISABLED_COLOR,
                                        fontSize: 18,
                                    }}
                                />
                            </Avatar>
                        </Tooltip>
                    </Box>
                ),
            },
            {
                title: t('Counter'),
                type: 'text' as const,
                field: 'counter',
                align: 'center' as const,
            },
            {
                title: t('Date'),
                align: 'center' as const,
                value: (row: Device) => formatDate(row.localDate),
            },
            {
                title: t('Facilities'),
                align: 'center' as const,
                value: (row: Device) => (
                    <SttChipFacilityRow facilities={row.facilities || []} />
                ),
            },
        ],
        [t, openDeviceDetails, formatDate],
    );

    return (
        <SttPage title={t('Devices')}>
            <SttTopControls
                p={2}
                fields={[
                    {
                        caption: t('Name'),
                        type: 'text',
                        name: 'name',
                        value: filters.name,
                        onEnterKey: loadDevices,
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                ]}
                onToggleTrash={toggleTrash}
                onChange={handleFilterChange}
                iconButtons={iconButtons}
                onFilter={loadDevices}
            />
            <SttTable onFetch={loadDevices} data={devices} columns={columns} />

            <DevicesKeysDialog
                openStatus={openKeysDialog}
                onClose={() => setOpenKeysDialog(false)}
            />

            <DevicesAuthorizationsDialog
                openStatus={openAuthorizations}
                onClose={() => setOpenAuthorizations(false)}
            />

            <DeviceDetailsDialog
                scId={String(cxt?.sc?.id || '')}
                deviceId={deviceIdSelected}
                onClose={closeDeviceDetails}
                initialTab={urlId ? getInitialTabFromUrl() : 0}
                onTabChange={urlId ? handleDeviceTabChange : undefined}
            />

            <DevicesSpacesDialog
                open={openDevicesSpaces}
                onClose={() => setOpenDevicesSpaces(false)}
            />
        </SttPage>
    );
}
